import React, { useEffect, useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import {
  loadOperatorPrefs,
  saveOperatorPrefs,
} from '../../utils/operatorPrefs';
import { patchUserPreferences } from '../../utils/userPreferencesApi';
import { getOrCreateTrialFingerprint } from '../../utils/trialCheckout';
import { signOutUser } from '../../utils/firebase';

/** Bump when copy/UX changes so free users see the gate again once. */
const ACK_KEY = 'pixlie.improve_model.ack_v2';

type Props = {
  /** Free / trial accounts cannot decline (product terms). Paid may decline. */
  planTier?: 'free' | 'trial' | 'paid';
  onResolved?: (accepted: boolean) => void;
};

/**
 * First-login blocker for Improve Model (Gemini-style acknowledgment).
 *
 * Leave free lane: sign out cleanly, then hard-navigate home — no auth bounce flash.
 */
export const ImproveModelConsentGate: React.FC<Props> = ({
  planTier = 'free',
  onResolved,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const locked = planTier === 'free' || planTier === 'trial';

  useEffect(() => {
    try {
      const acked = localStorage.getItem(ACK_KEY) === '1';
      const prefs = loadOperatorPrefs();
      if (!acked || (locked && !prefs.dataConsent)) {
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, [locked]);

  if (!open) return null;

  const persistServer = (improve_model: boolean) => {
    void patchUserPreferences({
      improve_model,
      trial_fingerprint: getOrCreateTrialFingerprint(),
    });
  };

  const accept = () => {
    if (busy) return;
    saveOperatorPrefs({ ...loadOperatorPrefs(), dataConsent: true });
    localStorage.setItem(ACK_KEY, '1');
    persistServer(true);
    setOpen(false);
    onResolved?.(true);
  };

  const leaveFreeLane = async () => {
    if (busy) return;
    setBusy(true);
    // Do not set ACK — next login should ask again. Sign out first so / does not bounce back.
    try {
      saveOperatorPrefs({ ...loadOperatorPrefs(), dataConsent: false });
      persistServer(false);
      await signOutUser();
    } catch {
      /* still leave the app shell */
    }
    onResolved?.(false);
    window.location.replace('/');
  };

  const declinePaid = () => {
    saveOperatorPrefs({ ...loadOperatorPrefs(), dataConsent: false });
    localStorage.setItem(ACK_KEY, '1');
    persistServer(false);
    setOpen(false);
    onResolved?.(false);
  };

  const bullets = [
    'Help improve Pixlie with anonymized prompts and outcomes (identifiers stripped).',
    'Your generations are not published as a named public dataset.',
    'Paid plans can turn this off anytime in Settings.',
  ];

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="improve-model-title"
    >
      <div
        className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--foreground) 32%, transparent)',
        }}
      />
      <div className="relative w-full max-w-md rounded-[28px] border border-border bg-card p-6 text-foreground shadow-pop sm:p-7">
        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-subtle text-brand">
          <Sparkles className="h-5 w-5" />
        </div>
        <h2 id="improve-model-title" className="text-xl font-semibold tracking-tight">
          Improve Pixlie models
        </h2>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Free and trial accounts help Pixlie get better by sharing{' '}
          <strong className="font-medium text-foreground">anonymized</strong> generation
          signals (prompts/outcomes with identifiers stripped). Paid plans can turn this off
          anytime in Settings.
        </p>

        <ul className="mt-5 space-y-3">
          {bullets.map((line) => (
            <li key={line} className="flex gap-3 text-sm leading-5 text-foreground/90">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-brand">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className="mt-4 text-xs leading-5 text-muted-foreground">
          This is part of the free-tier product terms — not a marketing checkbox. By continuing
          you agree to our{' '}
          <a href="/terms/" className="underline underline-offset-2 hover:text-foreground">
            Terms
          </a>{' '}
          and{' '}
          <a href="/privacy/" className="underline underline-offset-2 hover:text-foreground">
            Privacy
          </a>
          .
        </p>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (locked) void leaveFreeLane();
              else declinePaid();
            }}
            className="rounded-2xl border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-[var(--surface-2)] hover:text-foreground disabled:opacity-50"
          >
            {busy ? 'Leaving…' : locked ? 'Leave free lane' : 'Not now'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={accept}
            className="rounded-2xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
          >
            Continue with Improve Model
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImproveModelConsentGate;
